<script setup>
import { reactive, computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  itemType: {
    type: String,
    default: 'product',
    validator: value => ['product', 'service'].includes(value),
  },
  item: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const uiFlags = useMapGetter('captainCatalogItems/getUIFlags');

// Mirrors Captain::CatalogItem::MEDIA_SIZE_LIMITS. Checked here as well so a file that would
// be refused is caught while the admin is still looking at it, not after the item is saved.
const MB = 1024 * 1024;
const MEDIA_SIZE_LIMITS = {
  'image/jpeg': 5 * MB,
  'image/png': 5 * MB,
  'image/webp': 5 * MB,
  'video/mp4': 20 * MB,
  'application/pdf': 10 * MB,
};
const MAX_MEDIA_FILES = 5;
const MEDIA_ACCEPT = Object.keys(MEDIA_SIZE_LIMITS).join(',');

const typeKey = computed(() =>
  props.itemType === 'service' ? 'SERVICES' : 'PRODUCTS'
);

const initialState = {
  name: '',
  sku: '',
  price: '',
  currency: '',
  description: '',
  enabled: true,
};

const state = reactive({ ...initialState });

// The extras are an object on the wire and a list of rows on screen: an object cannot keep
// a half-typed key, and a row being edited has to survive until it is complete.
const customFieldRows = ref([]);

// Files are only sent once the item itself is saved, since a new item has no id to attach
// them to yet. Until then, what is already stored and what is queued are kept apart.
const existingMedia = ref([]);
const removedMediaIds = ref([]);
const pendingFiles = ref([]);
const fileInputRef = ref(null);

const toRows = customFields =>
  Object.entries(customFields || {}).map(([key, value]) => ({
    key,
    value: String(value ?? ''),
  }));

watch(
  () => props.item,
  newItem => {
    if (props.mode !== 'edit' || !newItem?.id) return;

    state.name = newItem.name || '';
    state.sku = newItem.sku || '';
    state.price = newItem.price ?? '';
    state.currency = newItem.currency || '';
    state.description = newItem.description || '';
    state.enabled = newItem.enabled !== false;
    customFieldRows.value = toRows(newItem.custom_fields);
    existingMedia.value = newItem.media || [];
  },
  { immediate: true }
);

const validationRules = {
  name: { required },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? uiFlags.value.updatingItem
    : uiFlags.value.creatingItem
);

const submitLabel = computed(() =>
  props.mode === 'edit' ? t('CAPTAIN.FORM.EDIT') : t('CAPTAIN.FORM.CREATE')
);

const nameError = computed(() =>
  v$.value.name.$error ? t('CAPTAIN.CATALOG_ITEMS.FORM.NAME.ERROR') : ''
);

const visibleMedia = computed(() =>
  existingMedia.value.filter(media => !removedMediaIds.value.includes(media.id))
);

const mediaCount = computed(
  () => visibleMedia.value.length + pendingFiles.value.length
);

const canAddMedia = computed(() => mediaCount.value < MAX_MEDIA_FILES);

const isImage = contentType => String(contentType).startsWith('image/');

const mediaIcon = contentType => {
  if (String(contentType).startsWith('video/')) return 'i-lucide-video';
  if (contentType === 'application/pdf') return 'i-lucide-file-text';
  return 'i-lucide-image';
};

const openFileDialog = () => {
  fileInputRef.value?.click();
};

const refusalFor = file => {
  const limit = MEDIA_SIZE_LIMITS[file.type];
  if (!limit) {
    return t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.INVALID_TYPE', {
      name: file.name,
    });
  }
  if (file.size > limit) {
    return t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.TOO_LARGE', {
      name: file.name,
      size: limit / MB,
    });
  }
  return '';
};

const handleFilesSelected = event => {
  const files = Array.from(event.target.files || []);
  // Cleared so choosing the same file again, after removing it, still fires a change.
  event.target.value = '';

  let limitReported = false;
  files.forEach(file => {
    if (mediaCount.value >= MAX_MEDIA_FILES) {
      if (!limitReported) {
        useAlert(
          t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.LIMIT_REACHED', {
            count: MAX_MEDIA_FILES,
          })
        );
        limitReported = true;
      }
      return;
    }

    const refusal = refusalFor(file);
    if (refusal) {
      useAlert(refusal);
      return;
    }

    pendingFiles.value.push({
      file,
      previewUrl: isImage(file.type) ? URL.createObjectURL(file) : null,
    });
  });
};

const revokePreview = entry => {
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
};

const removePendingFile = index => {
  revokePreview(pendingFiles.value[index]);
  pendingFiles.value.splice(index, 1);
};

const removeExistingMedia = id => {
  removedMediaIds.value.push(id);
};

onBeforeUnmount(() => {
  pendingFiles.value.forEach(revokePreview);
});

const addCustomField = () => {
  customFieldRows.value.push({ key: '', value: '' });
};

const removeCustomField = index => {
  customFieldRows.value.splice(index, 1);
};

// A row with no key has nothing to label its value with, so it never reaches the catalog.
const customFieldsPayload = () =>
  customFieldRows.value.reduce((acc, { key, value }) => {
    const trimmedKey = key.trim();
    if (trimmedKey) acc[trimmedKey] = value;
    return acc;
  }, {});

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', {
    item: {
      ...state,
      item_type: props.itemType,
      price: state.price === '' ? null : state.price,
      custom_fields: customFieldsPayload(),
    },
    pendingFiles: pendingFiles.value.map(entry => entry.file),
    removedMediaIds: [...removedMediaIds.value],
  });
};
</script>

<template>
  <form
    class="flex flex-col px-4 -mx-4 gap-4 max-h-[calc(100vh-200px)] overflow-y-scroll"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.CATALOG_ITEMS.FORM.NAME.LABEL')"
      :placeholder="t(`CAPTAIN.CATALOG_ITEMS.${typeKey}.NAME_PLACEHOLDER`)"
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
    />

    <Input
      v-model="state.sku"
      :label="t('CAPTAIN.CATALOG_ITEMS.FORM.SKU.LABEL')"
      :placeholder="t('CAPTAIN.CATALOG_ITEMS.FORM.SKU.PLACEHOLDER')"
    />

    <div class="flex gap-2">
      <Input
        v-model="state.price"
        type="number"
        min="0"
        :label="t('CAPTAIN.CATALOG_ITEMS.FORM.PRICE.LABEL')"
        :placeholder="t('CAPTAIN.CATALOG_ITEMS.FORM.PRICE.PLACEHOLDER')"
        class="flex-1"
      />
      <Input
        v-model="state.currency"
        :label="t('CAPTAIN.CATALOG_ITEMS.FORM.CURRENCY.LABEL')"
        :placeholder="t('CAPTAIN.CATALOG_ITEMS.FORM.CURRENCY.PLACEHOLDER')"
        class="w-28"
      />
    </div>

    <TextArea
      v-model="state.description"
      :label="t('CAPTAIN.CATALOG_ITEMS.FORM.DESCRIPTION.LABEL')"
      :placeholder="
        t(`CAPTAIN.CATALOG_ITEMS.${typeKey}.DESCRIPTION_PLACEHOLDER`)
      "
      :rows="3"
    />

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.LABEL') }}
        </label>
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-upload"
          :label="t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.ADD')"
          :disabled="!canAddMedia"
          @click="openFileDialog"
        />
      </div>
      <span class="text-sm text-n-slate-11">
        {{
          t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.HELP', { count: MAX_MEDIA_FILES })
        }}
      </span>
      <input
        ref="fileInputRef"
        type="file"
        multiple
        :accept="MEDIA_ACCEPT"
        class="hidden"
        @change="handleFilesSelected"
      />
      <div v-if="mediaCount" class="grid grid-cols-5 gap-2">
        <div
          v-for="media in visibleMedia"
          :key="`existing-${media.id}`"
          class="relative flex items-center justify-center overflow-hidden rounded-lg aspect-square bg-n-alpha-2"
          :title="media.filename"
        >
          <img
            v-if="isImage(media.content_type)"
            :src="media.url"
            :alt="media.filename"
            class="object-cover w-full h-full"
          />
          <span
            v-else
            :class="mediaIcon(media.content_type)"
            class="text-2xl text-n-slate-11"
          />
          <Button
            type="button"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-x"
            class="absolute top-1 ltr:right-1 rtl:left-1"
            @click="removeExistingMedia(media.id)"
          />
        </div>
        <div
          v-for="(entry, index) in pendingFiles"
          :key="`pending-${entry.file.name}-${index}`"
          class="relative flex items-center justify-center overflow-hidden rounded-lg aspect-square bg-n-alpha-2"
          :title="entry.file.name"
        >
          <img
            v-if="entry.previewUrl"
            :src="entry.previewUrl"
            :alt="entry.file.name"
            class="object-cover w-full h-full"
          />
          <span
            v-else
            :class="mediaIcon(entry.file.type)"
            class="text-2xl text-n-slate-11"
          />
          <Button
            type="button"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-x"
            class="absolute top-1 ltr:right-1 rtl:left-1"
            @click="removePendingFile(index)"
          />
        </div>
      </div>
      <span v-if="pendingFiles.length" class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.PENDING_NOTE') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.CATALOG_ITEMS.FORM.CUSTOM_FIELDS.LABEL') }}
        </label>
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          :label="t('CAPTAIN.CATALOG_ITEMS.FORM.CUSTOM_FIELDS.ADD')"
          @click="addCustomField"
        />
      </div>
      <span class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.CATALOG_ITEMS.FORM.CUSTOM_FIELDS.HELP') }}
      </span>
      <div
        v-for="(row, index) in customFieldRows"
        :key="index"
        class="flex items-end gap-2"
      >
        <Input
          v-model="row.key"
          :placeholder="
            t('CAPTAIN.CATALOG_ITEMS.FORM.CUSTOM_FIELDS.KEY_PLACEHOLDER')
          "
          class="w-40"
        />
        <Input
          v-model="row.value"
          :placeholder="
            t('CAPTAIN.CATALOG_ITEMS.FORM.CUSTOM_FIELDS.VALUE_PLACEHOLDER')
          "
          class="flex-1"
        />
        <Button
          type="button"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="removeCustomField(index)"
        />
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.CATALOG_ITEMS.FORM.ENABLED.LABEL') }}
        </label>
        <span class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.CATALOG_ITEMS.FORM.ENABLED.HELP') }}
        </span>
      </div>
      <Switch v-model="state.enabled" />
    </div>

    <div class="flex items-center justify-end w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="submitLabel"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
